"use client";

import { X, Plus, AlertCircle } from "lucide-react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { onCreateCatalogDraft } from "../../../redux/homeAction";
import { useState } from "react";

export default function UploadModal({
  open,
  images,
  setImages,
  onClose,
  selectedPath,
  navigate,
  setLoading,
}) {
  const dispatch = useDispatch();
  if (!open) return null;

  const createDraft = async () => {
    if (images.length === 0) {
      alert("At least 1 image required");
      return;
    }

    const last = selectedPath[selectedPath.length - 1];

    const formData = new FormData();
    formData.append("categoryId", last._id);

    images.forEach((img) => {
      formData.append("images", img.file);
    });

    dispatch(onCreateCatalogDraft(formData, setLoading, navigate));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);

    if (images.length >= 9) {
      alert("Maximum 9 images allowed");
      return;
    }

    const remainingSlots = 9 - images.length;

    const selectedFiles = files.slice(0, remainingSlots);

    const newImages = selectedFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));

    setImages([...images, ...newImages]);
  };

  const removeImage = (index) => {
    const updated = [...images];
    updated.splice(index, 1);
    setImages(updated);
  };

  const restrictions = [
    "Watermark image",
    "Fake branded/1st copy",
    "Image with price",
    "Pixelated image",
    "Inverted image",
    "Blur/unclear image",
    "Incomplete image",
    "Stretched/shrunk image",
    "Image with props",
    "Image with text",
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3">
      <div className="bg-white w-full max-w-4xl rounded-xl overflow-hidden max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="border-b px-4 sm:px-6 py-4">
          <h2 className="text-xl sm:text-2xl font-semibold">
            Products in a catalog
          </h2>
        </div>

        {/* Body */}
        <div className="p-4 sm:p-6">
          <p className="text-gray-600 text-sm sm:text-base mb-5">
            Please add only front image of your product. If you want to add
            multiple images for particular product, you can add it in next step.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Side */}
            <div>
              <div className="bg-yellow-50 border border-yellow-400 rounded-xl p-3 flex gap-2 mb-5">
                <AlertCircle
                  size={20}
                  className="text-yellow-500 shrink-0 mt-0.5"
                />
                <p className="font-medium text-sm sm:text-base text-gray-700">
                  You can add minimum 1 and maximum 9 products to create a
                  catalog
                </p>
              </div>

              <div className="flex flex-wrap gap-3">
                {images.map((img, index) => (
                  <div
                    key={index}
                    className="relative w-[90px] h-[115px] sm:w-[110px] sm:h-[140px] rounded-lg overflow-hidden border"
                  >
                    <img
                      src={img.preview}
                      alt=""
                      className="w-full h-full object-cover"
                    />

                    <button
                      onClick={() => removeImage(index)}
                      className="absolute top-1 right-1 bg-gray-600 text-white rounded-full p-1"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}

                {images.length < 9 && (
                  <label className="w-[90px] h-[115px] sm:w-[110px] sm:h-[140px] border-2 border-dashed border-indigo-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-indigo-50 transition">
                    <Plus size={28} className="text-indigo-600" />

                    <span className="mt-2 text-xs sm:text-sm text-indigo-700 font-medium text-center px-1">
                      Add Product
                    </span>

                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageUpload}
                    />
                  </label>
                )}
              </div>
            </div>

            {/* Right Side */}
            <div>
              <div className="flex items-center gap-2 text-red-500 font-semibold text-base sm:text-lg mb-5">
                <div className="w-6 h-6 rounded-full border-2 border-red-500 flex items-center justify-center text-red-500">
                  ✕
                </div>

                <span>Image types which are not allowed</span>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {restrictions.map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gray-100 rounded-lg shrink-0"></div>

                    <div>
                      <p className="font-medium text-sm text-gray-700">
                        {item}
                      </p>

                      <p className="text-xs text-gray-400 font-semibold">
                        ⦸ NOT ALLOWED
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex flex-col sm:flex-row justify-end gap-3">
          <button
            onClick={onClose}
            className="w-full sm:w-auto border border-gray-300 px-5 py-2.5 rounded-lg font-medium"
          >
            Cancel
          </button>

          <button
            onClick={createDraft}
            disabled={images.length === 0}
            className={`w-full sm:w-auto px-6 py-2.5 rounded-lg font-medium text-white ${
              images.length === 0
                ? "bg-indigo-300 cursor-not-allowed"
                : "bg-indigo-600 hover:bg-indigo-700"
            }`}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}
